import { useCallback } from 'react';
import { trpc } from '@client-trpc';
import type {
  SignalBlockTarget,
  TemplateType
} from '@server/trading-templates/schemas/input.schema';
import {
  TemplateApplyMode,
  useTemplateEvents
} from '../template-events';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import { useWindow } from '@client/window/window.hook';
import { templateNotifications } from '../template-notifications';

export const useApplyTemplate = () => {
  const { notify } = useNotifications();
  const { closeWindow } = useWindow();
  const trpcClient = trpc.useUtils().client;

  const { emitTemplateApplied } = useTemplateEvents();

  const onApply = useCallback(
    (
      timeframeIndex: number,
      templateType: TemplateType,
      strategyTarget?: SignalBlockTarget,
      applyMode: TemplateApplyMode = 'replace'
    ) =>
      async (templateIds: string[]) => {
        try {
          if (!templateIds.length) {
            return;
          }

          // Fetch templates for the specific type when needed
          const templates =
            await trpcClient.tradingTemplates.tradingTemplates.query({
              templateType
            });

          const templatesById = new Map(
            templates.map((template) => [template.id, template])
          );

          const appliedTemplate = templatesById.get(templateIds[0]);
          if (!appliedTemplate) {
            throw new Error('Template not found');
          }

          emitTemplateApplied({
            templateId: appliedTemplate.id,
            templateName: appliedTemplate.name,
            templateType,
            applyMode,
            timeframeIndex,
            strategyTarget,
            templateConfig: appliedTemplate.config
          });

          notify({
            type: 'success',
            message: templateNotifications.applied(applyMode)
          });

          closeWindow('ManageTradingTemplates');
        } catch (error) {
          notify({
            type: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Failed to apply template'
          });
        }
      },
    [notify, emitTemplateApplied, closeWindow, trpcClient]
  );

  return { onApply };
};
